// src/supabaseClient.js
import { createClient } from '@supabase/supabase-js';

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate that environment variables are set
if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL' || !supabaseUrl.includes('supabase.co')) {
  console.error('❌ REACT_APP_SUPABASE_URL is not configured correctly');
  console.error('Current value:', supabaseUrl);
  console.error('Expected: https://xxxxx.supabase.co');
  console.error('Please check your .env.local file or Vercel environment variables');
}

if (!supabaseAnonKey || supabaseAnonKey === 'YOUR_SUPABASE_ANON_KEY' || supabaseAnonKey.length < 100) {
  console.error('❌ REACT_APP_SUPABASE_ANON_KEY is not configured correctly');
  console.error('Expected: A long JWT token starting with eyJ...');
  console.error('Please check your .env.local file or Vercel environment variables');
}

// Create Supabase client with session persistence
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
      flowType: 'pkce'
    }
  }
);

// Helper function to handle Supabase errors
export const handleSupabaseError = (error, context = '') => {
  console.error(`Supabase Error ${context}:`, error);
  
  // Provide helpful error messages
  let errorMessage = error.message || 'An unknown error occurred';
  
  if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
    errorMessage = 'Cannot connect to Supabase. Please check your internet connection and Supabase URL.';
  } else if (errorMessage.includes('Invalid API key')) {
    errorMessage = 'Invalid Supabase API key. Please check your REACT_APP_SUPABASE_ANON_KEY environment variable.';
  } else if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
    errorMessage = 'Database tables not found. Please run the schema SQL in your Supabase dashboard.';
  }
  
  return {
    success: false,
    error: errorMessage
  };
};

// Helper to ensure we have an account ID
export const getAccountId = async () => {
  try {
    const { data, error } = await supabase
      .from('account')
      .select('id')
      .single();
    
    if (error) {
      console.error('Error fetching account:', error);
      
      // If table doesn't exist, provide helpful message
      if (error.message && error.message.includes('relation "account" does not exist')) {
        console.error('❌ Database tables not created. Please run supabase-schema.sql in your Supabase SQL Editor.');
      }
      
      return null;
    }
    
    return data?.id;
  } catch (err) {
    console.error('Exception getting account ID:', err);
    return null;
  }
};

// Validate connection on module load
if (supabaseUrl && supabaseAnonKey) {
  console.log('✅ Supabase client initialized');
  console.log('📍 URL:', supabaseUrl);
} else {
  console.warn('⚠️  Supabase not properly configured - check environment variables');
}
